import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../config";

const OldHomepage = () => {
  const [summary, setSummary] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState("");
  const [disabledFilter, setDisabledFilter] = useState("all"); // all | false | true
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Frontend-only paging (backend returns all)
  const [visibleCount, setVisibleCount] = useState(50);

  const loadUsers = async () => {
    try {
      setLoadingUsers(true);
      const token = localStorage.getItem("authToken");

      const params = new URLSearchParams();
      if (search.trim()) params.set("search", search.trim());
      if (disabledFilter === "true") params.set("disabled", "true");
      if (disabledFilter === "false") params.set("disabled", "false");

      const res = await axios.get(
        `${API}/api/old-homepage/users?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setUsers(res.data.users || []);
      setVisibleCount(50); // reset view to first 50 after any fetch
    } catch (err) {
      console.error(err);
      alert("Failed to load users");
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const token = localStorage.getItem("authToken");

        const summaryRes = await axios.get(`${API}/api/old-homepage/summary`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setSummary(summaryRes.data);

        await loadUsers(); // initial users load (all)
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When filter changes, reload users and reset visible count
  useEffect(() => {
    if (!loading) {
      loadUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabledFilter]);

  const toggleDisable = async (userId, isCurrentlyDisabled) => {
    try {
      const token = localStorage.getItem("authToken");
      const url = isCurrentlyDisabled
        ? `${API}/api/old-homepage/users/${userId}/enable`
        : `${API}/api/old-homepage/users/${userId}/disable`;

      const res = await axios.patch(
        url,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const updated = res.data.user;

      // update UI list
      setUsers((prev) =>
        prev.map((u) => (u._id === updated._id ? { ...u, ...updated } : u))
      );
    } catch (err) {
      console.error(err);
      alert("Failed to update user");
    }
  };

  const deleteUser = async (userId, username) => {
    const answer = window.prompt(
      `Type DELETE to permanently delete "${username}"`
    );
    if (answer !== "DELETE") return;

    try {
      const token = localStorage.getItem("authToken");

      await axios.delete(`${API}/api/old-homepage/users/${userId}?confirm=true`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // remove from UI
      setUsers((prev) => prev.filter((u) => u._id !== userId));
    } catch (err) {
      console.error(err);
      alert("Failed to delete user");
    }
  };

  if (loading) {
    return (
      <section className="admin">
        <p>Loading…</p>
      </section>
    );
  }

  const shownUsers = users.slice(0, visibleCount);

  return (
    <section className="admin">
      <h1 className="admin-title">Old Homepage</h1>

      {/* Summary cards */}
      {summary && (
        <div className="cards">
          <Card label="Total Users" value={summary.totalUsers} />
          <Card label="Disabled Users" value={summary.disabledUsers} />
          <Card label="Flagged Games" value={summary.flaggedGames} />
          <Card label="Games Played Last 24hrs" value={summary.gamesLast24h} />
        </div>
      )}

      {/* Controls */}
      <div className="admin-controls">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search username / id…"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              loadUsers();
            }
          }}
        />

        <select
          value={disabledFilter}
          onChange={(e) => setDisabledFilter(e.target.value)}
        >
          <option value="all">All</option>
          <option value="false">Active</option>
          <option value="true">Disabled</option>
        </select>

        <button
          className="btn-small ok"
          onClick={() => loadUsers()}
          disabled={loadingUsers}
        >
          {loadingUsers ? "..." : "Go"}
        </button>
      </div>

      {/* Users table */}
      <table className="admin-table">
        <thead>
          <tr>
            <th>Username</th>
            <th>Status</th>
            <th>Games Played</th>
            <th>Latest Score</th>
            <th>Highest Score</th>
            <th>Actions</th>
          </tr>
        </thead>

        <tbody>
          {shownUsers.map((u) => (
            <tr key={u._id}>
              <td>{u.username}</td>

              <td>
                {u.isDisabled ? (
                  <span className="pill pill-bad">Disabled</span>
                ) : (
                  <span className="pill pill-ok">Active</span>
                )}
              </td>

              <td>{u.gamesPlayed ?? 0}</td>
              <td>{u.latestScore ?? 0}</td>
              <td>{u.highestScore ?? 0}</td>

              <td className="actions">
                {!u.isDisabled ? (
                  <button
                    className="btn-small warn"
                    onClick={() => toggleDisable(u._id, false)}
                  >
                    Disable
                  </button>
                ) : (
                  <button
                    className="btn-small ok"
                    onClick={() => toggleDisable(u._id, true)}
                  >
                    Enable
                  </button>
                )}

                <button
                  className="btn-small danger"
                  onClick={() => deleteUser(u._id, u.username)}
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Footer / Show more */}
      <div className="admin-footer">
        <p className="admin-count">
          Showing {Math.min(visibleCount, users.length)} of {users.length} users
        </p>

        {users.length > visibleCount && (
          <div className="load-more">
            <button
              className="btn-small ok"
              onClick={() => setVisibleCount((v) => v + 50)}
              disabled={loadingUsers}
            >
              {loadingUsers ? "..." : "Show more"}
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

const Card = ({ label, value }) => (
  <div className="card">
    <div className="value">{value}</div>
    <div className="label">{label}</div>
  </div>
);

export default OldHomepage;
